import { z } from "zod";
import { zReadonlyArray } from "../zod/utils.js";

import { DisplayTableColumnId, DisplayTableConfigId } from "../idTypeBrands.js";

import { ColumnAggregationsDefinition } from "./aggregationTypes.js";
import { CalcsDefinition } from "./calcTypes.js";
import { ColumnDisplayFormat } from "./columnDisplayFormatTypes.js";
import { ConditionalFormattingDefinition } from "./conditionalFormattingTypes.js";
import { TableFiltersDefinition } from "./filterTypes.js";
import { SortDirectionLiteral } from "./outputTypes.js";
import { CellReferencesV2 } from "../code/cellReferencesV2.js";
import { CellReferencesParseError } from "../code/cellReferencesV1.js";

export const ColumnPropertyPayload = z.object({
  originalName: DisplayTableColumnId,
  displayFormat: ColumnDisplayFormat.optional(),
  renameTo: z.string().optional(),
  size: z.number().optional(),
  revision: z.number(),
  wrapText: z.boolean().optional(),
});

export const DisplayTableConfigPayload = z.object({
  id: DisplayTableConfigId,
  revision: z.number(),
  pageSize: z.number(),
  height: z.number().optional(),
  hideIcons: z.boolean(),
  defaultColumnWidth: z.union([z.number(), z.null()]).optional(),
  hideIndex: z.boolean(),
  sortByColumnDefault: z.string().optional(),
  sortByIndexColumnDefault: z.number().optional(),
  sortDirectionDefault: SortDirectionLiteral,
  calcs: CalcsDefinition.optional(),
  conditionalFormatting: ConditionalFormattingDefinition.optional(),
  filters: TableFiltersDefinition.optional(),
  columnProperties: zReadonlyArray(ColumnPropertyPayload),
  customColumnOrdering: zReadonlyArray(DisplayTableColumnId).optional(),
  pivotColumnOrdering: zReadonlyArray(
    z.union([SortDirectionLiteral, z.null()]),
  ).optional(),
  pinnedColumns: zReadonlyArray(z.string()).optional(),
  hiddenColumns: zReadonlyArray(z.string()).optional(),
  pinIndexColumns: z.boolean(),
  showAggregations: z.boolean().optional(),
  columnAggregations: ColumnAggregationsDefinition.optional(),
  conditionalFormattingReferences: z
    .union([CellReferencesV2, z.null()])
    .optional(),
  conditionalFormattingReferencesParseError: z
    .union([CellReferencesParseError, z.null()])
    .optional(),
  filtersReferences: z.union([CellReferencesV2, z.null()]).optional(),
  filtersReferencesParseError: z
    .union([CellReferencesParseError, z.null()])
    .optional(),
});

export type DisplayTableConfigPayload = z.infer<
  typeof DisplayTableConfigPayload
>;
